'use client'

import { ChevronDown } from 'lucide-react'

interface AccordionItemProps {
  /** Unique identifier used for aria-controls and aria-labelledby */
  id: string
  /** Title displayed in the accordion trigger button */
  title: string | React.ReactNode
  /** Whether the accordion panel is currently expanded */
  isOpen: boolean
  /** Callback when the trigger button is clicked */
  onToggle: () => void
  /** Content displayed when expanded */
  children: React.ReactNode
  /** Optional className for the outer wrapper div */
  className?: string
  /** Optional className for the trigger button */
  buttonClassName?: string
  /** Optional className for the title text */
  titleClassName?: string
  /** Optional className for the chevron icon */
  chevronClassName?: string
  /** Optional className for the content panel */
  contentClassName?: string
}

export default function AccordionItem({
  id,
  title,
  isOpen,
  onToggle,
  children,
  className,
  buttonClassName = 'w-full flex items-center justify-between py-4 md:py-5 text-left outline-none focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:ring-offset-2 rounded',
  titleClassName = 'text-sm md:text-base font-semibold text-gray-900 pr-4',
  chevronClassName = 'w-5 h-5 text-gray-400 flex-shrink-0',
  contentClassName = 'pb-4',
}: AccordionItemProps) {
  const triggerId = `accordion-trigger-${id}`
  const panelId = `accordion-panel-${id}`

  return (
    <div className={className}>
      <button
        id={triggerId}
        type="button"
        onClick={onToggle}
        className={buttonClassName}
        aria-expanded={isOpen}
        aria-controls={panelId}
      >
        <span className={titleClassName}>{title}</span>
        <ChevronDown
          className={`${chevronClassName} transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        />
      </button>

      <div
        id={panelId}
        role="region"
        aria-labelledby={triggerId}
        className={`overflow-hidden transition-all duration-200 ${
          isOpen ? `max-h-[2000px] ${contentClassName}` : 'max-h-0'
        }`}
      >
        {children}
      </div>
    </div>
  )
}
